import React from 'react';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

/**
 * Inline (i) icon that reveals a help tooltip on hover/tap. Use next to any
 * non-obvious button, field label, or option so the rep can get a sentence
 * or two of context without leaving the page.
 *
 * Usage:
 *   <InfoTip>Recreates the Meet event and emails both parties.</InfoTip>
 *   <InfoTip side="bottom" size={3.5}>...</InfoTip>
 *
 * Stays accessible: the tooltip is keyboard-focusable, dismissible with Esc,
 * and announces via aria-describedby (radix handles all that).
 */
export function InfoTip({ children, side = 'top', size = 3.5, className }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger
          asChild
          // tabIndex 0 so the icon is keyboard-reachable. type=button so it
          // doesn't accidentally submit a surrounding form.
        >
          <button
            type="button"
            className={cn(
              'inline-flex shrink-0 items-center justify-center text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-ring focus:ring-offset-1 rounded-full',
              className,
            )}
            aria-label="More info"
          >
            <Info className={`size-${size}`} />
          </button>
        </TooltipTrigger>
        {/* Case/weight reset lives on TooltipContent itself - see tooltip.jsx. */}
        <TooltipContent side={side} className="max-w-xs text-xs">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
